import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Factory, FileText, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import useAuthStore from "@/store/authStore";

// FG Production Log — Jul 2026, parity with RM Production Log. Standalone
// screen (no "produce FG here" flow — FG completions are stamped by
// Branch Ops when a schedule is marked Complete). Reads
// GET /api/fg-production/log with the same filter / pagination
// contract as the RM log.
const API = process.env.REACT_APP_BACKEND_URL;

const FGProductionLog = () => {
  const { token } = useAuthStore();

  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branch: "", buyer_sku_id: "", start_date: "", end_date: "",
  });
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Feb 2026 — build the Authorization header INSIDE each callback
  // from `token`. The previous top-level `const auth = { headers: ... }`
  // was rebuilt on every render and pulled into the useCallback deps,
  // recreating the callbacks on every render, retriggering the
  // useEffects, refetching, setState, re-render — an infinite fetch
  // loop that pinned the page on "Loading…" with hundreds of pending
  // /api/branches + /api/fg-production/log requests.
  const loadBranches = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches((r.data || []).map(b => b.name || b));
    } catch {
      setBranches([]);
    }
  }, [token]);

  const loadLog = useCallback(async () => {
    // Feb 2026 — token-null early-return trims the initial 2-call
    // dupe seen on first mount (effect fires before token hydration
    // completes → an anonymous request that 401s, then a second
    // authorised one). Harmless before but wastes a round-trip.
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "50" });
      if (filters.branch) params.set("branch", filters.branch);
      if (filters.buyer_sku_id) params.set("buyer_sku_id", filters.buyer_sku_id);
      if (filters.start_date) params.set("start_date", filters.start_date);
      if (filters.end_date) params.set("end_date", filters.end_date);
      const r = await axios.get(`${API}/api/fg-production/log?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load FG production log");
    } finally {
      setLoading(false);
    }
  }, [filters, page, token]);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { loadLog(); }, [loadLog]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
  };

  return (
    <div className="p-6 space-y-6" data-testid="fg-production-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="w-6 h-6 text-orange-500" />
            FG Production Log
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            History of finished-goods production completions from Branch Ops schedules.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" /> Production Entries
              </CardTitle>
              <CardDescription>
                {total} total {total === 1 ? "entry" : "entries"} matching your filters
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-[200px]">
              <Label className="text-xs">Branch</Label>
              <Select
                value={filters.branch || "all"}
                onValueChange={(v) => { setFilters(f => ({ ...f, branch: v === "all" ? "" : v })); setPage(1); }}
              >
                <SelectTrigger data-testid="branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Label className="text-xs">SKU</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-400" />
                <Input
                  className="pl-8"
                  value={filters.buyer_sku_id}
                  onChange={(e) => { setFilters(f => ({ ...f, buyer_sku_id: e.target.value })); setPage(1); }}
                  placeholder="Buyer SKU ID"
                  data-testid="sku-filter"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date" value={filters.start_date}
                onChange={(e) => { setFilters(f => ({ ...f, start_date: e.target.value })); setPage(1); }}
                data-testid="start-date-filter"
              />
            </div>
            <div className="w-[160px]">
              <Label className="text-xs">End Date</Label>
              <Input
                type="date" value={filters.end_date}
                onChange={(e) => { setFilters(f => ({ ...f, end_date: e.target.value })); setPage(1); }}
                data-testid="end-date-filter"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => { setFilters({ branch: "", buyer_sku_id: "", start_date: "", end_date: "" }); setPage(1); }}
                data-testid="clear-filters-btn"
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Schedule Code</TableHead>
                  <TableHead>Completed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-500">Loading…</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-zinc-500">No production entries match these filters.</TableCell></TableRow>
                ) : items.map((it, i) => (
                  <TableRow key={i} data-testid={`fg-log-row-${i}`}>
                    <TableCell className="text-xs font-mono">{formatDate(it.produced_at)}</TableCell>
                    <TableCell>{it.branch}</TableCell>
                    <TableCell className="font-mono text-xs">{it.buyer_sku_id}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{it.sku_description || "—"}</TableCell>
                    <TableCell className="text-right font-medium">{it.quantity}</TableCell>
                    <TableCell className="font-mono text-xs">{it.schedule_code || "—"}</TableCell>
                    <TableCell className="text-sm text-zinc-600">{it.completed_by || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FGProductionLog;
